import { useState, useEffect, useRef } from "react";
import config from "../config";

const API = config.API_BASE_URL;
const BOT_NAME = "ORCA Bot";

// ── ORCA headset SVG avatar ───────────────────────────────────
const OrcaAvatar = ({ size = 28 }) => (
  <div style={{
    width: size, height: size, borderRadius: "50%",
    background: "linear-gradient(135deg,#0084ff,#006fde)",
    display: "flex", alignItems: "center", justifyContent: "center",
    flexShrink: 0,
  }}>
    <svg width={size * 0.58} height={size * 0.58} viewBox="0 0 24 24" fill="white">
      <path d="M12 2C6.48 2 2 6.48 2 12v3c0 1.1.9 2 2 2h1c1.1 0 2-.9 2-2v-2c0-1.1-.9-2-2-2H4.07C4.56 7.19 7.92 4 12 4s7.44 3.19 7.93 7H19c-1.1 0-2 .9-2 2v2c0 1.1.9 2 2 2h1c1.1 0 2-.9 2-2v-3c0-5.52-4.48-10-10-10z"/>
    </svg>
  </div>
);

// ── Greeting messages ─────────────────────────────────────────
const GREETING_BUBBLES = [
  { id: 1, text: "👋 Hi there! Welcome to ORCA Helpdesk!", delay: 0 },
  { id: 2, text: "I'm your virtual support assistant.", delay: 600 },
  { id: 3, text: "I can help you submit a ticket or answer common questions!", delay: 1200 },
];

// ── Quick reply options ───────────────────────────────────────
const QUICK_REPLIES = [
  { id: "ticket", label: "📋 Submit a Ticket",     msg: "I want to submit a support ticket." },
  { id: "status", label: "🔍 Check Ticket Status", msg: "I want to check my ticket status." },
  { id: "faq",    label: "❓ Common Questions",     msg: "What are your common questions?" },
  { id: "human",  label: "🧑‍💼 Talk to a Human",      msg: "I'd like to speak to a live agent." },
];

// ── Bot scripted responses ────────────────────────────────────
const BOT_RESPONSES = {
  ticket: [
    "Sure! To submit a ticket, please describe your issue below and I'll create one for you right away. 📋",
    "Our support team will be assigned to your case within minutes. You can track your ticket status anytime from your dashboard.",
  ],
  status: [
    "To check your ticket status, head over to your Dashboard → My Tickets. 🔍",
    "You can see real-time updates, admin replies, and resolution status there!",
  ],
  faq: [
    "Here are the most common questions we receive:",
    "• How do I reset my password?\n• How long does a ticket take to resolve?\n• Can I attach files to my ticket?\n• How do I escalate my issue?",
    "Type your specific question and I'll do my best to help! 😊",
  ],
  human: [
    "Of course! 🧑‍💼 I'll connect you to a live support agent.",
    "Please wait a moment while I notify the team. You can also type your concern below and an agent will reply as soon as they're available!",
  ],
  default: [
    "Got it! I'm creating your request now... ✨",
    "A support agent will reach out to you shortly. In the meantime, feel free to add more details!",
  ],
};

// ── Typing dots ───────────────────────────────────────────────
function TypingIndicator() {
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 6, marginTop: 8 }}>
      <OrcaAvatar size={28} />
      <div style={{ background: "#f0f2f5", borderRadius: "4px 18px 18px 18px", padding: "10px 16px", display: "flex", gap: 5, alignItems: "center" }}>
        {[0, 1, 2].map(i => (
          <span key={i} style={{ width: 7, height: 7, borderRadius: "50%", background: "#b0b3b8", display: "inline-block", animation: `botDot 1.2s ease-in-out ${i * 0.2}s infinite` }} />
        ))}
      </div>
    </div>
  );
}

// ── Bot bubble ────────────────────────────────────────────────
function BotBubble({ text, visible }) {
  if (!visible) return null;
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 6, marginTop: 6, animation: "botUp 0.3s cubic-bezier(.34,1.56,.64,1)" }}>
      <OrcaAvatar size={28} />
      <div style={{ background: "#f0f2f5", color: "#050505", borderRadius: "4px 18px 18px 18px", padding: "9px 13px", fontSize: 13.5, lineHeight: 1.5, maxWidth: "78%", whiteSpace: "pre-wrap", wordBreak: "break-word", boxShadow: "0 1px 2px rgba(0,0,0,0.08)" }}>
        {text}
      </div>
    </div>
  );
}

// ── User bubble ───────────────────────────────────────────────
function UserBubble({ text }) {
  return (
    <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 6, animation: "botUp 0.25s ease" }}>
      <div style={{ background: "#0084ff", color: "#fff", borderRadius: "18px 4px 18px 18px", padding: "9px 13px", fontSize: 13.5, lineHeight: 1.5, maxWidth: "78%", wordBreak: "break-word", boxShadow: "0 1px 4px rgba(0,132,255,0.3)" }}>
        {text}
      </div>
    </div>
  );
}

// ── Main AIChatBot ────────────────────────────────────────────
export default function AIChatBot({ userData, onHandoff, autoOpen = false }) {
  const [open, setOpen]             = useState(autoOpen);
  const [phase, setPhase]           = useState("greeting");
  const [visibleGreets, setVisible] = useState([]);
  const [messages, setMessages]     = useState([]);
  const [typing, setTyping]         = useState(false);
  const [quickReplies, setQuick]    = useState([]);
  const [inputVal, setInputVal]     = useState("");
  const [unread, setUnread]         = useState(0);
  const bottomRef                   = useRef(null);
  const msgId                       = useRef(0);
  const nextId                      = () => ++msgId.current;

  // ── Show greeting bubbles ─────────────────────────────────
  useEffect(() => {
    if (!open || phase !== "greeting") return;
    GREETING_BUBBLES.forEach(({ id, delay }) => {
      setTimeout(() => setVisible(prev => [...prev, id]), delay + 400);
    });
  }, [open]);

  // ── Scroll to bottom ──────────────────────────────────────
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, typing, visibleGreets, quickReplies]);

  // ── Post message to backend so admin can see it ───────────
  const postToBackend = async (content) => {
    if (!userData?.username) return;
    try {
      await fetch(`${API}/Messages/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user: userData.username,
          admin: "admin",
          content,
        }),
      });
    } catch (err) {
      console.warn("Bot backend post failed:", err);
    }
  };

  // ── Add bot message with delay ────────────────────────────
  const addBotMsg = (text, delay = 0, showTyping = true) => {
    if (showTyping && delay > 0) setTimeout(() => setTyping(true), Math.max(0, delay - 800));
    setTimeout(() => {
      setTyping(false);
      setMessages(prev => [...prev, { id: nextId(), from: "bot", text }]);
    }, delay);
  };

  // ── Get Started clicked ───────────────────────────────────
  const handleGetStarted = () => {
    setPhase("started");
    setUnread(0);
    const firstName = userData?.fullNames?.split(" ")[0] ?? "there";
    addBotMsg(`Great! 🎉 Nice to meet you, ${firstName}! What can I help you with today?`, 0, false);
    setTimeout(() => setQuick(QUICK_REPLIES), 600);
  };

  // ── Quick reply chosen ────────────────────────────────────
  const handleQuickReply = (item) => {
    setQuick([]);
    setMessages(prev => [...prev, { id: nextId(), from: "user", text: item.label }]);
    // ✅ Post concern to backend — admin will see this in chat
    postToBackend(`[Bot Concern] ${item.msg}`);

    const responses = BOT_RESPONSES[item.id] ?? BOT_RESPONSES.default;
    responses.forEach((text, i) => addBotMsg(text, 900 + i * 1100, i === 0));

    const handoffDelay = 900 + responses.length * 1100 + 400;
    setTimeout(() => {
      setTyping(false);
      setMessages(prev => [...prev, { id: nextId(), from: "bot", text: "Would you like to continue chatting with a live support agent? 💬", isHandoff: true }]);
      setPhase("replied");
    }, handoffDelay);
  };

  // ── Free text send ────────────────────────────────────────
  const handleSend = () => {
    const text = inputVal.trim();
    if (!text) return;
    setInputVal("");
    setMessages(prev => [...prev, { id: nextId(), from: "user", text }]);
    // ✅ Post to backend
    postToBackend(text);
    setQuick([]);

    let intent = "default";
    const lower = text.toLowerCase();
    if (lower.includes("ticket") || lower.includes("issue") || lower.includes("problem")) intent = "ticket";
    else if (lower.includes("status") || lower.includes("check") || lower.includes("update")) intent = "status";
    else if (lower.includes("faq") || lower.includes("question") || lower.includes("help")) intent = "faq";
    else if (lower.includes("human") || lower.includes("agent") || lower.includes("live") || lower.includes("person")) intent = "human";

    const responses = BOT_RESPONSES[intent];
    responses.forEach((t, i) => addBotMsg(t, 900 + i * 1100, i === 0));

    const handoffDelay = 900 + responses.length * 1100 + 400;
    setTimeout(() => {
      setMessages(prev => [...prev, { id: nextId(), from: "bot", text: "Would you like to continue chatting with a live support agent? 💬", isHandoff: true }]);
      setPhase("replied");
    }, handoffDelay);
  };

  // ── Handoff to live agent ─────────────────────────────────
  const handleHandoff = () => {
    addBotMsg("Connecting you now... 🔗 A support agent will take it from here!", 0, false);
    setTimeout(() => {
      setOpen(false);
      if (onHandoff) onHandoff();
    }, 1200);
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  return (
    <>
      <style>{`
        @keyframes botDot { 0%,60%,100%{transform:translateY(0)} 30%{transform:translateY(-5px)} }
        @keyframes botUp  { from{transform:translateY(10px);opacity:0} to{transform:translateY(0);opacity:1} }
        @keyframes botPop { from{transform:scale(0.7);opacity:0} to{transform:scale(1);opacity:1} }
        .bot-scroll::-webkit-scrollbar{width:4px}
        .bot-scroll::-webkit-scrollbar-thumb{background:#c4c9d4;border-radius:4px}
      `}</style>

      <div style={{ position: "fixed", bottom: 24, right: 24, zIndex: 10003 }}>

        {/* ── Chat window ── */}
        {open && (
          <div style={{ position: "absolute", bottom: 68, right: 0, width: 340, height: 500, background: "#fff", borderRadius: 20, border: "1px solid #e4e6eb", boxShadow: "0 10px 40px rgba(0,0,0,0.18)", display: "flex", flexDirection: "column", overflow: "hidden", animation: "botPop 0.25s cubic-bezier(.34,1.56,.64,1)" }}>

            {/* Header */}
            <div style={{ padding: "12px 16px", background: "linear-gradient(135deg,#0084ff,#006fde)", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ position: "relative" }}>
                  <OrcaAvatar size={38} />
                  <span style={{ position: "absolute", bottom: 1, right: 1, width: 10, height: 10, borderRadius: "50%", background: "#31a24c", border: "2px solid #006fde" }} />
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 13.5, color: "#fff" }}>{BOT_NAME}</div>
                  <div style={{ fontSize: 10.5, color: "rgba(255,255,255,0.85)" }}>● Always here to help</div>
                </div>
              </div>
              <button onClick={() => setOpen(false)} style={{ background: "rgba(255,255,255,0.18)", border: "none", color: "#fff", borderRadius: "50%", width: 30, height: 30, cursor: "pointer", fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
            </div>

            {/* Messages */}
            <div className="bot-scroll" style={{ flex: 1, overflowY: "auto", padding: "14px 14px 10px", display: "flex", flexDirection: "column" }}>
              {GREETING_BUBBLES.map(({ id, text }) => (
                <BotBubble key={id} text={text} visible={visibleGreets.includes(id)} />
              ))}

              {phase === "greeting" && visibleGreets.length === GREETING_BUBBLES.length && (
                <div style={{ display: "flex", justifyContent: "center", marginTop: 18, animation: "botPop 0.3s ease" }}>
                  <button onClick={handleGetStarted}
                    style={{ background: "linear-gradient(135deg,#0084ff,#006fde)", color: "#fff", border: "none", borderRadius: 30, padding: "11px 28px", fontSize: 14, fontWeight: 700, cursor: "pointer", letterSpacing: 0.3, boxShadow: "0 4px 16px rgba(0,132,255,0.4)", transition: "transform 0.15s" }}
                    onMouseEnter={e => e.currentTarget.style.transform = "scale(1.05)"}
                    onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}
                  >🚀 Get Started!</button>
                </div>
              )}

              {messages.map(m => (
                <div key={m.id}>
                  {m.from === "bot" ? <BotBubble text={m.text} visible={true} /> : <UserBubble text={m.text} />}
                  {m.isHandoff && phase === "replied" && (
                    <div style={{ display: "flex", gap: 8, marginTop: 8, marginLeft: 34, flexWrap: "wrap", animation: "botPop 0.3s ease" }}>
                      <button onClick={handleHandoff} style={{ background: "#0084ff", color: "#fff", border: "none", borderRadius: 20, padding: "7px 16px", fontSize: 12.5, fontWeight: 700, cursor: "pointer", boxShadow: "0 2px 8px rgba(0,132,255,0.35)" }}>
                        Yes, connect me! 💬
                      </button>
                      <button onClick={() => setPhase("done")} style={{ background: "#f0f2f5", color: "#050505", border: "none", borderRadius: 20, padding: "7px 16px", fontSize: 12.5, fontWeight: 600, cursor: "pointer" }}>
                        No thanks
                      </button>
                    </div>
                  )}
                </div>
              ))}

              {typing && <TypingIndicator />}

              {quickReplies.length > 0 && (
                <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 10, marginLeft: 34, animation: "botUp 0.3s ease" }}>
                  {quickReplies.map(qr => (
                    <button key={qr.id} onClick={() => handleQuickReply(qr)}
                      style={{ background: "#fff", color: "#0084ff", border: "1.5px solid #0084ff", borderRadius: 20, padding: "8px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer", textAlign: "left", transition: "all 0.15s" }}
                      onMouseEnter={e => e.currentTarget.style.background = "#e8f4ff"}
                      onMouseLeave={e => e.currentTarget.style.background = "#fff"}
                    >{qr.label}</button>
                  ))}
                </div>
              )}

              <div ref={bottomRef} />
            </div>

            {/* Input bar */}
            {phase !== "greeting" && (
              <div style={{ padding: "8px 12px", borderTop: "1px solid #e4e6eb", background: "#fff", display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                <div style={{ flex: 1, background: "#f0f2f5", borderRadius: 22, padding: "9px 14px" }}>
                  <input value={inputVal} onChange={e => setInputVal(e.target.value)} onKeyDown={handleKey}
                    placeholder="Type a message..."
                    style={{ width: "100%", border: "none", outline: "none", background: "transparent", fontSize: 13.5, color: "#050505", fontFamily: "system-ui,sans-serif" }} />
                </div>
                <button onClick={handleSend} disabled={!inputVal.trim()}
                  style={{ background: inputVal.trim() ? "#0084ff" : "#e4e6eb", border: "none", borderRadius: "50%", width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center", cursor: inputVal.trim() ? "pointer" : "default", flexShrink: 0, transition: "background 0.2s" }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill={inputVal.trim() ? "#fff" : "#aaa"}><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/></svg>
                </button>
              </div>
            )}
          </div>
        )}

        {/* ── Floating bubble ── */}
        <button onClick={() => { setOpen(v => !v); setUnread(0); }}
          style={{ width: 56, height: 56, borderRadius: "50%", background: "linear-gradient(135deg,#0084ff,#006fde)", border: "none", cursor: "pointer", boxShadow: "0 4px 20px rgba(0,132,255,0.5)", display: "flex", alignItems: "center", justifyContent: "center", transition: "transform 0.2s", position: "relative" }}
          onMouseEnter={e => e.currentTarget.style.transform = "scale(1.1)"}
          onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}
        >
          {open
            ? <svg width="22" height="22" fill="#fff" viewBox="0 0 24 24"><path d="M7.41 15.41L12 10.83l4.59 4.58L18 14l-6-6-6 6z"/></svg>
            : <svg width="26" height="26" viewBox="0 0 24 24" fill="white">
                <path d="M12 2C6.48 2 2 6.48 2 12v3c0 1.1.9 2 2 2h1c1.1 0 2-.9 2-2v-2c0-1.1-.9-2-2-2H4.07C4.56 7.19 7.92 4 12 4s7.44 3.19 7.93 7H19c-1.1 0-2 .9-2 2v2c0 1.1.9 2 2 2h1c1.1 0 2-.9 2-2v-3c0-5.52-4.48-10-10-10z"/>
              </svg>
          }
          {!open && unread > 0 && (
            <div style={{ position: "absolute", top: -4, right: -4, background: "#e41e3f", color: "#fff", borderRadius: "50%", width: 20, height: 20, fontSize: 10, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", border: "2px solid #fff" }}>
              {unread > 9 ? "9+" : unread}
            </div>
          )}
        </button>
      </div>
    </>
  );
}